import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Save, Sparkles, X } from 'lucide-react';
import { BACKEND_URL as API } from '../../apiConfig';

const endpoint = `${API}/api/internal/dispatch/extra-schedules`;
const inputClass = 'w-full min-w-0 rounded-md border border-amber-300/25 bg-black p-2 text-white';
const fields = [
  ['date', '出发日期', 'date', true], ['time', '出发时间', 'time', true],
  ['pickup_location', '出发具体地址', 'text', true], ['dropoff_location', '结束具体地址', 'text', true],
  ['customer_name', '客户姓名', 'text'], ['phone', '联系电话', 'text'],
  ['flight_number', '航班号', 'text'], ['vehicle_type', '车型', 'text'],
  ['passenger_count', '乘客人数', 'number'], ['luggage_count', '行李数量', 'number'],
  ['child_seat_count', '儿童座椅数量', 'number'], ['infant_seat_count', '婴儿座椅数量（每个¥2,000）', 'number'], ['price_amount', '金额', 'number', true],
];
const numeric = ['price_amount', 'passenger_count', 'luggage_count', 'child_seat_count', 'infant_seat_count', 'selected_hours'];

export default function InternalExtraScheduleModal({ drivers, schedule, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    driver_id: '', service_type: 'transfer', price_currency: 'JPY', receipt_status: 'unpaid',
    raw_text: '', notes: '', ...schedule,
  }));
  const requestId = useRef(crypto.randomUUID());
  const locked = useRef(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [conflictPayload, setConflictPayload] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const change = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  async function parse() {
    if (locked.current) return;
    locked.current = true;
    setBusy('parse'); setError('');
    try {
      const { data } = await axios.post(`${endpoint}/parse`, { raw_text: form.raw_text }, { timeout: 0 });
      if (!data.success) throw new Error(data.error || '识别失败，请手动填写');
      const allowed = [...fields.map(([key]) => key), 'service_type', 'selected_hours', 'notes', 'price_currency'];
      setForm(prev => ({ ...prev, ...Object.fromEntries(allowed.filter(key => data.parsed?.[key] != null).map(key => [key, data.parsed[key]])),
        parse_model: data.parse_model, parse_status: data.parse_status, parse_confidence: data.confidence }));
    } catch (err) {
      setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : err.message);
    } finally { locked.current = false; setBusy(''); }
  }

  async function save(payload) {
    if (locked.current) return;
    locked.current = true; setBusy('save'); setError('');
    try {
      const { data } = schedule
        ? await axios.put(`${endpoint}/${schedule.id}`, payload)
        : await axios.post(endpoint, payload);
      onSaved(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === 'schedule_conflict') {
        setConflictPayload(payload); setConflicts(detail.conflicts);
      } else {
        setError(typeof detail === 'string' ? detail : '保存失败，请检查填写内容后重试');
      }
    } finally { locked.current = false; setBusy(''); }
  }

  function submit(event) {
    event.preventDefault();
    const payload = { ...Object.fromEntries([...fields.map(([key]) => key), 'driver_id', 'service_type',
      'selected_hours', 'notes', 'raw_text', 'price_currency', 'receipt_status', 'parse_model', 'parse_status', 'parse_confidence']
      .map(key => [key, form[key] ?? null])), request_id: requestId.current,
      expected_updated_at: schedule?.updated_at || null };
    for (const key of numeric) payload[key] = form[key] === '' || form[key] == null ? null : Number(form[key]);
    if (payload.service_type !== 'charter') payload.selected_hours = null;
    save(payload);
  }

  return createPortal(<div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/75 p-3">
    <section role="dialog" aria-modal="true" aria-labelledby="internal-extra-title" className="flex max-h-[94vh] w-full max-w-3xl flex-col rounded-lg border border-amber-300/30 bg-[#101010] text-white">
      <header className="flex items-center justify-between border-b border-amber-300/20 p-4">
        <h2 id="internal-extra-title" className="text-lg font-semibold">{schedule ? '修改调度追加' : '追加订单'}</h2>
        <button type="button" aria-label="关闭" title="关闭" disabled={!!busy} onClick={onClose} className="p-2"><X size={20} /></button>
      </header>
      <form onSubmit={submit} className="overflow-y-auto p-4">
        <fieldset disabled={!!busy || !!conflictPayload} className="space-y-4 disabled:opacity-70">
          <label className="block text-sm">预约原文<textarea aria-label="预约原文" maxLength={8000} rows={4} className={`${inputClass} mt-1`} value={form.raw_text || ''} onChange={e => change('raw_text', e.target.value)} /></label>
          <button type="button" onClick={parse} disabled={(form.raw_text || '').trim().length < 5} className="flex items-center gap-2 rounded-md border border-amber-300/40 px-3 py-2 disabled:opacity-50"><Sparkles size={16} />{busy === 'parse' ? '识别中…' : 'Grok AI识别'}</button>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="min-w-0 text-sm">司机<select aria-label="司机" required className={`${inputClass} mt-1`} value={form.driver_id} onChange={e => change('driver_id', e.target.value)}>
              <option value="">请选择司机</option>{drivers.map(driver => <option key={driver.driver_id} value={driver.driver_id}>{driver.driver_name}</option>)}
            </select></label>
            <label className="min-w-0 text-sm">服务类型<select aria-label="服务类型" className={`${inputClass} mt-1`} value={form.service_type} onChange={e => change('service_type', e.target.value)}><option value="transfer">单程接送</option><option value="charter">包车</option></select></label>
            {fields.map(([key, label, type, required]) => <label key={key} className="min-w-0 text-sm">{label}<input className={`${inputClass} mt-1`} type={type} required={required} value={form[key] ?? ''}
              min={type === 'number' ? 0 : undefined} step={type === 'number' ? (key === 'price_amount' ? (form.price_currency === 'JPY' ? 1 : 0.01) : 1) : undefined}
              onChange={e => change(key, e.target.value)} /></label>)}
            {form.service_type === 'charter' && <label className="text-sm">包车时长（小时）<input required type="number" min="0.1" max="168" step="0.1" className={`${inputClass} mt-1`} value={form.selected_hours ?? ''} onChange={e => change('selected_hours', e.target.value)} /></label>}
            <label className="text-sm">币种<select aria-label="币种" className={`${inputClass} mt-1`} value={form.price_currency} onChange={e => change('price_currency', e.target.value)}><option value="JPY">日元 JPY</option><option value="CNY">人民币 CNY</option></select></label>
            <label className="text-sm">收款标记<select aria-label="收款标记" className={`${inputClass} mt-1`} value={form.receipt_status} onChange={e => change('receipt_status', e.target.value)}><option value="unpaid">未收款</option><option value="paid">已收款</option></select></label>
          </div>
          <label className="block text-sm">行程内容／备注<textarea rows={4} className={`${inputClass} mt-1`} value={form.notes || ''} onChange={e => change('notes', e.target.value)} /></label>
          <button type="submit" className="flex items-center gap-2 rounded-md bg-amber-200 px-4 py-2 font-semibold text-black"><Save size={16} />{busy === 'save' ? '保存中…' : '保存'}</button>
        </fieldset>
        {conflictPayload && <div role="alert" className="mt-4 space-y-3 rounded-md border border-red-400 p-3">
          <p>该司机已有时间冲突的排班，仍要保存吗？</p>
          {conflicts.map(item => <p className="break-all text-sm" key={item.id}>{item.date} {item.time} · {item.id}</p>)}
          <div className="flex flex-wrap gap-3"><button type="button" disabled={!!busy} onClick={() => { setConflictPayload(null); setConflicts([]); }}>返回修改</button>
            <button type="button" disabled={!!busy} onClick={() => save({ ...conflictPayload, conflict_confirmed: true })}>确认冲突并保存</button></div>
        </div>}
        {error && <p role="alert" className="mt-3 text-red-300">{error}</p>}
      </form>
    </section>
  </div>, document.body);
}
